/**
 * QueryCache - Stores filtered, ordered query results separately from GraphStore
 *
 * Two-level caching architecture:
 * - GraphStore: Stores all loaded nodes/relationships as unordered Sets (O(1) operations)
 * - QueryCache: Stores query-specific filtered/ordered results as Arrays (server ordering)
 *
 * Key design decisions:
 * - Accept ID duplication between GraphStore and QueryCache (minimal memory cost)
 * - GraphStore uses Sets (unordered, for all loaded data)
 * - QueryCache uses Arrays (ordered, for specific query results)
 * - Both use Jotai atoms for reactivity
 *
 * Usage:
 * - useNodes(relationship) - Access all loaded relationships from GraphStore (undefined order)
 * - useResults(relationship, queryKey, property) - Access filtered/ordered query results from QueryCache
 */

import { atom, createStore } from 'jotai';
import { atomFamily } from 'jotai-family';
import { RelationshipId } from './graph-store';

export type QueryKey = string;
export type TraversalPath = string; // e.g., "steps", "steps.requirements"

/**
 * Query metadata tracking filters, ordering, and cache state
 */
export interface QueryMetadata {
  filters?: Record<string, any>;
  orderBy?: string[];
  limit?: number;
  skip?: number;
  complete: boolean; // Has the full result set been loaded?
  stale: boolean; // Should this be refreshed?
  ttl: Date; // When should this expire?
}

/**
 * Query result data structure
 * Maps traversal paths to ordered lists of relationship IDs
 */
export interface QueryResultData {
  // Root nodes (if query returns nodes directly)
  nodeIds: string[];

  // Relationship traversals: path -> ordered relationship IDs
  // e.g., "steps" -> ["thread-1::step-1", "thread-1::step-2"]
  relationships: Map<TraversalPath, RelationshipId[]>;

  metadata: QueryMetadata;
}

/**
 * Atom family for query results at specific traversal paths
 * Key format: "queryKey:traversalPath"
 */
export const queryResultAtomFamily = atomFamily((key: string) =>
  atom<RelationshipId[]>([])
);

/**
 * Atom family for query metadata
 */
export const queryMetadataAtomFamily = atomFamily((queryKey: QueryKey) =>
  atom<QueryMetadata>({
    complete: false,
    stale: false,
    ttl: new Date(Date.now() + 5 * 60 * 1000), // 5 minutes default
  })
);

/**
 * QueryCache singleton for managing query-specific results
 */
export class QueryCache {
  private static instance: QueryCache;

  // Query storage: queryKey -> QueryResultData
  private queries: Map<QueryKey, QueryResultData> = new Map();

  // Track which atoms have been used (for clearing in tests)
  private usedResultAtoms: Set<string> = new Set();
  private usedMetadataAtoms: Set<QueryKey> = new Set();

  // Jotai store for query atoms (separate from graphStore's store)
  public jotaiStore = createStore();

  private constructor() {}

  /**
   * Get the singleton instance
   */
  static getInstance(): QueryCache {
    if (!QueryCache.instance) {
      QueryCache.instance = new QueryCache();
    }
    return QueryCache.instance;
  }

  /**
   * Clear all cached queries (useful for testing or logout)
   */
  clear(): void {
    // Clear all result atoms
    this.usedResultAtoms.forEach(atomKey => {
      const atom = queryResultAtomFamily(atomKey);
      this.jotaiStore.set(atom, []);
    });

    // Clear all metadata atoms
    this.usedMetadataAtoms.forEach(queryKey => {
      const atom = queryMetadataAtomFamily(queryKey);
      this.jotaiStore.set(atom, {
        complete: false,
        stale: false,
        ttl: new Date(Date.now() + 5 * 60 * 1000),
      });
    });

    // Clear data structures
    this.queries.clear();

    // Clear tracking sets
    this.usedResultAtoms.clear();
    this.usedMetadataAtoms.clear();
  }

  /**
   * Store query results
   * @param queryKey Unique identifier for this query
   * @param data Query result data including node IDs, relationships, and metadata
   */
  setQueryResult(queryKey: QueryKey, data: QueryResultData): void {
    // Store in map
    this.queries.set(queryKey, data);

    // Update metadata atom
    this.usedMetadataAtoms.add(queryKey);
    this.jotaiStore.set(queryMetadataAtomFamily(queryKey), data.metadata);

    // Update relationship atoms for each traversal path
    data.relationships.forEach((relIds, traversalPath) => {
      const atomKey = this.makeAtomKey(queryKey, traversalPath);
      this.usedResultAtoms.add(atomKey);
      this.jotaiStore.set(queryResultAtomFamily(atomKey), relIds);
    });
  }

  /**
   * Get query result data
   * @param queryKey The query key
   * @returns QueryResultData or undefined if not cached
   */
  getQueryResult(queryKey: QueryKey): QueryResultData | undefined {
    return this.queries.get(queryKey);
  }

  /**
   * Get relationship IDs for a specific traversal path in a query
   * @param queryKey The query key
   * @param traversalPath The relationship traversal path (e.g., "steps")
   * @returns Ordered array of relationship IDs
   */
  getRelationshipIds(queryKey: QueryKey, traversalPath: TraversalPath): RelationshipId[] {
    const query = this.queries.get(queryKey);
    return query?.relationships.get(traversalPath) || [];
  }

  /**
   * Add relationships to a traversal path (append to ordered list)
   * Used when loading more results (pagination)
   * @param queryKey The query key
   * @param traversalPath The relationship traversal path
   * @param relIds Relationship IDs to append
   */
  appendRelationships(
    queryKey: QueryKey,
    traversalPath: TraversalPath,
    relIds: RelationshipId[]
  ): void {
    const query = this.queries.get(queryKey);
    if (!query) {
      throw new Error(`Query not found: ${queryKey}`);
    }

    // Get existing or create new relationship array
    const existing = query.relationships.get(traversalPath) || [];
    const updated = [...existing, ...relIds];

    // Update map
    query.relationships.set(traversalPath, updated);

    // Update atom
    const atomKey = this.makeAtomKey(queryKey, traversalPath);
    this.usedResultAtoms.add(atomKey);
    this.jotaiStore.set(queryResultAtomFamily(atomKey), updated);
  }

  /**
   * Mark a query as stale (needs refresh)
   * @param queryKey The query key
   */
  markStale(queryKey: QueryKey): void {
    const query = this.queries.get(queryKey);
    if (query) {
      query.metadata.stale = true;
      this.jotaiStore.set(queryMetadataAtomFamily(queryKey), query.metadata);
    }
  }

  /**
   * Invalidate a query (remove from cache)
   * @param queryKey The query key
   */
  invalidate(queryKey: QueryKey): void {
    const query = this.queries.get(queryKey);
    if (!query) return;

    // Clear all relationship atoms for this query
    query.relationships.forEach((_, traversalPath) => {
      const atomKey = this.makeAtomKey(queryKey, traversalPath);
      this.jotaiStore.set(queryResultAtomFamily(atomKey), []);
    });

    // Clear metadata atom
    this.jotaiStore.set(queryMetadataAtomFamily(queryKey), {
      complete: false,
      stale: false,
      ttl: new Date(Date.now() + 5 * 60 * 1000),
    });

    // Remove from map
    this.queries.delete(queryKey);
  }

  /**
   * Get atom for a relationship list at a specific traversal path
   * Used by useResults() hook for reactivity
   * @param queryKey The query key
   * @param traversalPath The relationship traversal path
   */
  getRelationshipListAtom(queryKey: QueryKey, traversalPath: TraversalPath) {
    const atomKey = this.makeAtomKey(queryKey, traversalPath);
    return queryResultAtomFamily(atomKey);
  }

  /**
   * Get atom for query metadata
   * @param queryKey The query key
   */
  getMetadataAtom(queryKey: QueryKey) {
    return queryMetadataAtomFamily(queryKey);
  }

  /**
   * Create atom key from queryKey and traversalPath
   * Format: "queryKey:traversalPath"
   */
  private makeAtomKey(queryKey: QueryKey, traversalPath: TraversalPath): string {
    if (queryKey === undefined || queryKey === null) {
      console.error('makeAtomKey called with undefined/null queryKey:', { queryKey, traversalPath });
      return `undefined:${traversalPath}`;
    }
    if (traversalPath === undefined || traversalPath === null) {
      console.error('makeAtomKey called with undefined/null traversalPath:', { queryKey, traversalPath });
      return `${queryKey}:undefined`;
    }
    return `${queryKey}:${traversalPath}`;
  }

  /**
   * Debug: Get cache info
   */
  getDebugInfo(): {
    queryCount: number;
    queries: Map<QueryKey, QueryResultData>;
  } {
    return {
      queryCount: this.queries.size,
      queries: new Map(this.queries),
    };
  }
}

// Export singleton instance
export const queryCache = QueryCache.getInstance();
